import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Tabs } from 'antd';
import { useRecoilValue } from 'recoil';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import type { Dayjs } from 'dayjs';

import { searchCmpBox } from '../../recoilatoms/searchCmp';
import AdminBoardService from '../../service/AdminBoardService';

import SearchInput from '../atom/SearchInput';
import TitleHeader from '../modecules/TitleHeader';
import TableList from '../table/TableList';
import { TAB_TYPE } from '../../constants';
import type { TabType, VocBoardItem, ServiceBoardItem } from '../../types';
import { formatDateToCustomString } from 'src/util/changeDate';

// ========== Styled Components ==========

const AdminVocBoardWrapper = styled.div`
  width: 70%;
  margin-top: 20px;
  position: relative;
`;

const AdminVocBoardSearchWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

// ========== Component ==========

const AdminVocBoardPage: React.FC = () => {
  const navigate = useNavigate();
  const searchCmp = useRecoilValue(searchCmpBox);

  // 데이터 상태
  const [dataList, setDataList] = useState<(VocBoardItem | ServiceBoardItem)[]>([]);
  const [filterboard, setFilterboard] = useState<(VocBoardItem | ServiceBoardItem)[]>([]);
  const [totalLength, setTotalLength] = useState(20);
  const [loading, setLoading] = useState(false);

  // 탭 및 인증
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const savedTab = sessionStorage.getItem('adminActiveTab');
    return (savedTab === TAB_TYPE.SERVICE ? TAB_TYPE.SERVICE : TAB_TYPE.VOC) as TabType;
  });
  const [adminPassword] = useState(() => sessionStorage.getItem('pw'));

  // VOC 탭 필터/검색
  const [progress, setProgress] = useState('4');
  const [nameKeyword, setNameKeyword] = useState('');
  const [titleKeyword, setTitleKeyword] = useState('');
  const [empNmKeyword, setEmpNmKeyword] = useState('');

  // 관리자 비밀번호 (null이면 빈 문자열로 처리)
  const adminPasswordValue = adminPassword || '';

  // SERVICE 탭 필터/검색
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [corpTypeFilter, setCorpTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [corpNameKeyword, setCorpNameKeyword] = useState('');
  const [bizRegNoKeyword, setBizRegNoKeyword] = useState('');
  const [serviceTitleKeyword, setServiceTitleKeyword] = useState('');
  const [contactNameKeyword, setContactNameKeyword] = useState('');

  // 날짜 선택
  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [value, setValue] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const _initialKeywordMount = useRef(true);
  const _initialServiceMount = useRef(true);
  const _initialDateMount = useRef(true);

  // ========== 날짜 관련 핸들러 ==========

  const disabledDate = (current: Dayjs) => {
    if (!dates) return false;
    const tooLate = dates[0] && current.diff(dates[0], 'days') >= 365;
    const tooEarly = dates[1] && dates[1].diff(current, 'days') >= 365;
    return !!tooEarly || !!tooLate;
  };

  const onOpenChange = (open: boolean) => {
    if (open) {
      setDates([null, null]);
    } else {
      setDates(null);
    }
  };

  const rangPickerHandler = (e: [Dayjs | null, Dayjs | null] | null) => {
    if (!e || !e[0] || !e[1]) {
      setStartDate('');
      setEndDate('');
      return;
    }
    const formattedDate = formatDateToCustomString([e[0].toDate(), e[1].toDate()]);
    setStartDate(formattedDate[0]);
    setEndDate(formattedDate[1]);
  };

  // ========== VOC 데이터 조회 ==========

  const getVocDataList = useCallback(
    async (pageArg = 0, pageSizeArg = 15) => {
      setLoading(true);
      try {
        const response = await AdminBoardService.getVocBoardList({
          page: pageArg,
          size: pageSizeArg,
          adminPassword: adminPasswordValue,
          progress,
          nameKeyword,
          titleKeyword,
          empNmKeyword,
          startDate,
          endDate
        });
        if (response.status === 'success' && 'data' in response && response.data !== null) {
          setDataList(response.data.content);
          setFilterboard(response.data.content);
          setTotalLength(response.data.total);
        } else {
          navigate('/AdminVocLogin');
        }
        return response;
      } finally {
        setLoading(false);
      }
    },
    [
      adminPasswordValue,
      progress,
      nameKeyword,
      titleKeyword,
      empNmKeyword,
      startDate,
      endDate,
      navigate
    ]
  );

  // ========== SERVICE 데이터 조회 ==========

  const getServiceDataList = useCallback(
    async (pageArg = 0, pageSizeArg = 15) => {
      setLoading(true);
      try {
        const response = await AdminBoardService.getServiceBoardList({
          page: pageArg,
          size: pageSizeArg,
          requestType: serviceTypeFilter === 'all' ? '' : serviceTypeFilter,
          corpType: corpTypeFilter === 'all' ? '' : corpTypeFilter,
          status: statusFilter === 'all' ? '' : statusFilter,
          corpName: corpNameKeyword || '',
          bizRegNo: bizRegNoKeyword || '',
          title: serviceTitleKeyword || '',
          contactName: contactNameKeyword || '',
          startDate,
          endDate
        });
        if (response.status === 'success' && 'data' in response && response.data !== null) {
          setDataList(response.data.content || []);
          setFilterboard(response.data.content || []);
          setTotalLength(response.data.totalElements || response.data.total || 0);
        } else {
          setDataList([]);
          setFilterboard([]);
          setTotalLength(0);
        }
        return response;
      } finally {
        setLoading(false);
      }
    },
    [
      serviceTypeFilter,
      corpTypeFilter,
      statusFilter,
      corpNameKeyword,
      bizRegNoKeyword,
      serviceTitleKeyword,
      contactNameKeyword,
      startDate,
      endDate
    ]
  );

  // ========== 통합 데이터 조회 ==========

  const getDataList = useCallback(
    async (pageArg = 0, pageSizeArg = 15) => {
      if (activeTab === TAB_TYPE.SERVICE) {
        return getServiceDataList(pageArg, pageSizeArg);
      }
      return getVocDataList(pageArg, pageSizeArg);
    },
    [activeTab, getVocDataList, getServiceDataList]
  );

  // ========== 검색 핸들러 ==========

  const handleSearch = (searchText: string) => {
    if (activeTab === TAB_TYPE.SERVICE) {
      // SERVICE 탭 검색
      setServiceTitleKeyword(searchCmp === '제목' ? searchText : '');
      setCorpNameKeyword(searchCmp === '법인명' ? searchText : '');
      setBizRegNoKeyword(searchCmp === '사업자등록번호' ? searchText : '');
      setContactNameKeyword(searchCmp === '작성자명' ? searchText : '');
    } else {
      // VOC 탭 검색
      setNameKeyword(searchCmp === '작성자' ? searchText : '');
      setTitleKeyword(searchCmp === '제목' ? searchText : '');
      setEmpNmKeyword(searchCmp === '조치담당자' ? searchText : '');
    }
  };

  // ========== 탭 변경 핸들러 ==========

  const handleTabChange = (key: string) => {
    setActiveTab(key as TabType);
    sessionStorage.setItem('adminActiveTab', key);
    // 탭 변경 시 필터/키워드 초기화
    setValue(null);
    setStartDate('');
    setEndDate('');
    if (key === TAB_TYPE.SERVICE) {
      setServiceTypeFilter('all');
      setCorpTypeFilter('all');
      setStatusFilter('all');
      setCorpNameKeyword('');
      setBizRegNoKeyword('');
      setServiceTitleKeyword('');
      setContactNameKeyword('');
    } else {
      setTitleKeyword('');
      setNameKeyword('');
      setEmpNmKeyword('');
    }
  };

  // ========== Effects ==========

  // VOC 키워드 변경 시 검색
  useEffect(() => {
    if (_initialKeywordMount.current) {
      _initialKeywordMount.current = false;
      return;
    }
    if (activeTab === TAB_TYPE.VOC) {
      getVocDataList(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameKeyword, titleKeyword, empNmKeyword]);

  // SERVICE 키워드 변경 시 검색
  useEffect(() => {
    if (_initialServiceMount.current) {
      _initialServiceMount.current = false;
      return;
    }
    if (activeTab === TAB_TYPE.SERVICE) {
      getServiceDataList(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpNameKeyword, bizRegNoKeyword, serviceTitleKeyword, contactNameKeyword]);

  // 날짜 범위 변경 시 검색
  useEffect(() => {
    if (_initialDateMount.current) {
      _initialDateMount.current = false;
      return;
    }
    if (startDate && endDate) {
      getDataList(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // 탭/필터 변경 시 데이터 조회
  useEffect(() => {
    if (sessionStorage.getItem('isAdmin') === 'true') {
      getDataList(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress, activeTab, serviceTypeFilter, corpTypeFilter, statusFilter]);

  // 필터링 적용
  useMemo(() => {
    if (activeTab === TAB_TYPE.SERVICE) {
      setFilterboard(dataList || []);
    } else {
      const filterBoard = (dataList || []).filter(el => {
        const vocItem = el as VocBoardItem;
        if (progress === '4') {
          return ['0', '1', '2', '3', '5'].includes(String(vocItem.accept));
        }
        return String(vocItem.accept) === String(progress);
      });
      setFilterboard(filterBoard);
    }
  }, [dataList, progress, activeTab]);

  // ========== Render ==========

  return (
    <AdminVocBoardWrapper>
      <TitleHeader title="관리자 통합 게시판" />
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          { key: TAB_TYPE.VOC, label: 'VOC' },
          { key: TAB_TYPE.SERVICE, label: '서비스 신청/문의' }
        ]}
      />
      <AdminVocBoardSearchWrap>
        <SearchInput
          onSearchHandler={handleSearch}
          value={value}
          onDateChange={val => {
            setValue(val);
            rangPickerHandler(val);
          }}
          onDateOpenChange={onOpenChange}
          disabledDate={disabledDate}
          activeTab={activeTab}
          progress={progress}
          onStatusChange={val => setProgress(val)}
          serviceTypeFilter={serviceTypeFilter}
          onServiceTypeChange={val => setServiceTypeFilter(val)}
          corpTypeFilter={corpTypeFilter}
          onCorpTypeChange={val => setCorpTypeFilter(val)}
          statusFilter={statusFilter}
          onStatusFilterChange={val => setStatusFilter(val)}
        />
      </AdminVocBoardSearchWrap>

      <TableList
        filterboard={filterboard}
        totalLength={totalLength}
        activeTab={activeTab}
        loading={loading}
        onPageChange={(newPage, newSize) => getDataList(newPage, newSize)}
      />
    </AdminVocBoardWrapper>
  );
};

export default AdminVocBoardPage;
